import {
  useEffect, useRef, useState
} from 'react';
import useSWR from 'swr';

import fetchAssayResults from '../../utils/api_functions/assayResultRequest';

import { mapAssayResults } from '../../utils/assay_utils';
import { true_result_from_v1 } from './V2Assays/conversions';
import useClientContext from '../contexts/ClientData/ClientData';
import { AssayResults } from '../../types/assay.types';

export const useAssayResults = ({
  assayID,
  assayConfig,
  startTimestamp,
  endTimestamp,
  shouldFetchResults = () => null,
  validationDelay = 500,
}) => {
  // This is a timeout ref, but importing this type causes compilation errors with typescript
  const errorTimeoutRef = useRef<any>(null);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [errorMessage, setError] = useState<string|null>(null);

  const [currentData, setCurrentData] = useState<AssayResults[]>([]);
  const { isAssaysV2Enabled } = useClientContext();

  const queryKey = `assay-results-${assayID}`
  const {
    data,
    error,
    mutate,
  } = useSWR(
    () => shouldFetchResults() ? queryKey : null,
    () => fetchAssayResults([assayID, startTimestamp, endTimestamp, isAssaysV2Enabled]),
    {
      refreshInterval: 3000,
      loadingTimeout: validationDelay,
      onLoadingSlow: () => setLoading(true),
      onSuccess: () => setLoading(false),
      onError: () => setLoading(false),
    }
  );

  useEffect(() => {
    if (data?.length) {
      if (isAssaysV2Enabled) {
        setCurrentData(data?.map(assayResult => true_result_from_v1(assayResult, assayConfig)));
      } else {
        /**
         * @deprecated Assays V1 Logic
         */
        setCurrentData(data?.map(mapAssayResults));
      }
    } else {
      setCurrentData([]);
    }
  }, [data, assayConfig, isAssaysV2Enabled])

  useEffect(() => {
    if (error) {
      errorTimeoutRef.current = setTimeout(() => {
        setError(error?.message);
      }, validationDelay);
    } else {
      clearTimeout(errorTimeoutRef.current);
      setError(null);
    }

    return () => {
      clearTimeout(errorTimeoutRef.current);
      setError(null);
    }
  }, [error, validationDelay]);

  return {
    data: currentData,
    error: errorMessage,
    isLoading,
    refetchAssayResults: mutate,
  };
}

export default useAssayResults;
